import classNames from 'classnames';
import { createRef } from 'react';
import { Icon } from 'palette/Icon';
import { BodyText, Typography } from 'palette/Typography';
import { consumeTranslations } from '@common/Translations';
import FormControl from './FormControl';
import './Text.scss';

export class TextComponent extends FormControl {
    constructor(props) {
        super(props);

        this.componentRef = createRef();
        this.setValue = this.setValue.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
    }

    handleBlur() {
        this.validate({ hasFocus: false, isDirty: true });
    }

    handleFocus(event) {
        const { onFocus } = this.props;
        this.setState({ hasFocus: true });

        if (typeof onFocus === 'function') {
            onFocus(event);
        }
    }

    setValue(event) {
        this.props.onChange(event);
    }

    getError() {
        const { error } = this.state;

        return this.isErrorVisible() && error ? error : null;
    }

    generateThemeClassNames() {
        const { theme } = this.props;

        if (Array.isArray(theme)) {
            return theme.map(item => `text--theme-${item}`);
        }

        return [`text--theme-${theme}`];
    }

    render() {
        const {
            theme,
            name,
            value,
            propertyName,
            width,
            type = 'text',
            required,
            disabled,
            customClass,
            isGridItem,
            readOnly,
            label,
            autoComplete,
            placeholder,
            inputMode,
            enterKeyHint,
            color = 'strong',
            weight = 'bold',
            autoFocus,
            forwardedRef,
            iconName,
            iconSize = 'small',
            iconColor = 'grey-600',
            tabIndex = 0
        } = this.props;
        const { type: stateType } = this.state;

        const inputAttrs = {
            ...(inputMode && { inputMode }),
            ...(autoComplete && { autoComplete }),
            ...(propertyName && { 'data-property': propertyName }),
            ...(readOnly && { tabIndex: -1 }),
            ...(enterKeyHint && { enterKeyHint })
        };

        const error = this.getError();
        const labelType = this.state.hasFocus || value ? 'small' : 'body';
        const themes = theme && this.generateThemeClassNames();
        const descriptiveElementId = `input-desc-${name || label?.replace(/\s+/g, '-').toLowerCase()}`;
        const isRequired = this.props.validators?.includes('required');

        return (
            <label
                ref={this.componentRef}
                className={classNames(
                    'text',
                    customClass,
                    {
                        [`text--${stateType}`]: this.isErrorVisible(),
                        [`text--width${width}`]: width,
                        'text--grid-item': isGridItem
                    },
                    themes
                )}
                htmlFor={name}
            >
                <BodyText
                    className="text__typography-container"
                    color={color}
                    tagName="div"
                    weight={weight}
                >
                    { iconName
                        && (
                            <Icon
                                className="text__icon"
                                color={iconColor}
                                name={iconName}
                                size={iconSize}
                            />
                        )}
                    <input
                        ref={forwardedRef}
                        aria-describedby={descriptiveElementId}
                        aria-invalid={!!error}
                        aria-required={isRequired}
                        autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
                        className="skip-generic-styling"
                        disabled={disabled}
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        required={required}
                        tabIndex={tabIndex}
                        type={type}
                        value={value}
                        onBlur={this.handleBlur}
                        onChange={this.setValue}
                        onFocus={this.handleFocus}
                        {...inputAttrs}
                    />
                </BodyText>
                <Typography
                    color="light"
                    type={labelType}
                >
                    {label}
                </Typography>
                {error && (
                    <Typography
                        className="text__error"
                        color="attention"
                        id={descriptiveElementId}
                        level="2"
                        type="small"
                    >
                        {error}
                    </Typography>
                )}
            </label>
        );
    }
}

export default consumeTranslations(TextComponent);
